import React from 'react';
import PropTypes from 'prop-types';
import './ui.css';

/** The KPI card: sections side by side, divided by hairlines. */
export function KpiBand({ label, children, className }) {
    return (
        <section className={['v2-kpis', className].filter(Boolean).join(' ')} aria-label={label}>
            {children}
        </section>
    );
}

KpiBand.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, className: PropTypes.string };

/** One section: uppercase title, optional info, right-aligned meta, a bar, then content. */
export function KpiSection({ title, info, meta, bar, children, className }) {
    return (
        <div className={['v2-kpi', className].filter(Boolean).join(' ')}>
            <div className="v2-kpi-head">
                <span className="v2-kpi-title">{title}</span>
                {info}
                {meta && <span className="v2-kpi-meta">{meta}</span>}
            </div>
            {bar}
            {children}
        </div>
    );
}

KpiSection.propTypes = {
    title: PropTypes.node.isRequired,
    info: PropTypes.node,
    meta: PropTypes.node,
    bar: PropTypes.node,
    children: PropTypes.node,
    className: PropTypes.string,
};

/** Proportional stacked bar (decorative; the buckets carry the numbers). */
export function StackBar({ segments }) {
    return (
        <div className="v2-stackbar" aria-hidden="true">
            {segments.filter((s) => s.value > 0).map((s) => (
                <span key={s.key || s.tone} className={`v2-tone-${s.tone}`} style={{ flex: s.value }} />
            ))}
        </div>
    );
}

StackBar.propTypes = {
    segments: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        tone: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
    })).isRequired,
};

/** Grid of buckets, `columns` across. */
export function Buckets({ columns = 4, children }) {
    return <div className="v2-buckets" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>{children}</div>;
}

Buckets.propTypes = { columns: PropTypes.number, children: PropTypes.node };

/**
 * A KPI figure. With `onClick` it is a filter toggle (aria-pressed); without
 * it, a static figure.
 */
export function Bucket({ label, value, sub, tone, pressed = false, onClick, title, className, ...rest }) {
    const body = (
        <>
            <span className="v2-bk-n">{value}</span>
            <span className="v2-bk-l">
                {tone && <i className={`v2-bk-dot v2-tone-${tone}`} aria-hidden="true" />}
                {label}
            </span>
            {sub && <span className="v2-bk-p">{sub}</span>}
        </>
    );
    const cls = ['v2-bk', className].filter(Boolean).join(' ');
    if (!onClick) return <div className={cls} title={title} {...rest}>{body}</div>;
    return (
        <button type="button" className={cls} aria-pressed={pressed} onClick={onClick} title={title} {...rest}>
            {body}
        </button>
    );
}

Bucket.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.node.isRequired,
    sub: PropTypes.node,
    tone: PropTypes.oneOf(['sage', 'slate', 'amber', 'red', 'teal', 'grey']),
    pressed: PropTypes.bool,
    onClick: PropTypes.func,
    title: PropTypes.string,
    className: PropTypes.string,
};
